// https://www.annuities.pacificlife.com/enroll-ui/retail
import React, { useState, useEffect } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import ReactTooltip from "react-tooltip";
import Toast from "react-bootstrap/Toast";
import axios from "axios";
import moment from "moment";
import DatePicker from "react-datepicker";
import { QuestionCircle, Eye, EyeSlash } from "react-bootstrap-icons";
import formatSSN from "utils/formatSSN";
import formatZip from "utils/formatZip";
import {
    RETAIL_FP,
    REQUIRED_FIELDS,
    ANNUITIES_FP_LOGIN_PAGE_URL_PATH,
    ANNUITIES_GET_APPOINTED_URL,
    ANNUITIES_CONTACT_US_URL,
    REGISTRATION_SUCCESSFUL_URL_PATH
} from "./constants";
import {
    emailValidation,
    userNameValidation,
    emailVerifyMatch,
    ssnValidation
} from "./validation";
import { analytics } from "./analytics";
import { Header, Footer, Disclaimer } from "./components";

const RetailReg = () => {
    const [user, setUser] = useState({
        role: "", // NO
        firstName: "",
        lastName: "",
        email: "",
        verifyEmail: "",
        firmId: "", //NO
        crd: "", //NO
        contractNumber: "",
        tin: "",
        dob: "",
        id: "",
        optionSelect: null,
        zipCode: ""
    });
    const [errors, setErrors] = useState({
        firmId: "",
        crd: "",
        email: "",
        verifyEmail: "",
        id: "",
        tin: "",
        contractNumber: ""
    });
    const [toastMessage, setToastMessage] = useState("");
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showTin, setShowTin] = useState(false);

    useEffect(() => {
        analytics();
    }, []);

    useEffect(() => {
        // check if all fields are filled out
        const role = RETAIL_FP;
        const option = user.optionSelect;
        const requiredFields = REQUIRED_FIELDS[role];
        for (const fieldName of requiredFields) {
            if (!user[fieldName]) {
                setSubmitDisabled(true);
                return;
            }
            if (option === "notAppointed") {
                setSubmitDisabled(true);
                return;
            }
            if (option === "CN") {
                if (user.contractNumber.length < 1) {
                    setSubmitDisabled(true);
                    return;
                }
            }
            if (option === "DOB") {
                if (user.tin.length < 9 || user.zipCode.length < 5) {
                    setSubmitDisabled(true);
                    return;
                }
            }
        }
        // check if there is any error
        const errorFields = Object.keys(errors);
        for (const fieldName of errorFields) {
            if (errors[fieldName] !== "") {
                setSubmitDisabled(true);
                return;
            }
        }
        setSubmitDisabled(false);
    }, [user, errors]);

    useEffect(() => {
        document.title = "Retail Registration | Pacific Life";
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const payload = { [name]: value };
        setUser((prev) => ({ ...prev, ...payload }));
        if (name === "optionSelect") {
            if (value === "CN") {
                setUser((prev) => ({ ...prev, zipCode: "", dob: "" }));
            } else if (value === "DOB") {
                setUser((prev) => ({ ...prev, contractNumber: "" }));
            } else if (value === "notAppointed") {
                setUser((prev) => ({
                    ...prev,
                    contractNumber: "",
                    zipCode: "",
                    dob: ""
                }));
            }
        }
    };

    const handleBlur = async (e) => {
        const { name, value } = e.target;
        let errorMessage = "";
        if (name === "email") {
            const emailValid = emailValidation(value);
            errorMessage = emailValid
                ? ""
                : "Please enter a valid email address";
        }
        if (name === "verifyEmail") {
            errorMessage = emailVerifyMatch(user.email, value)
                ? ""
                : "Email addresses do not match";
        }
        if (name === "id") {
            const valid = userNameValidation(value);
            const tooShortOrLong = value.length < 6 || value.length > 8;
            const hasSpecialCharOrWhiteSpace = /\W|\D/g.test(value);
            if (valid) {
                errorMessage = "";
            } else if (tooShortOrLong) {
                errorMessage =
                    "User name must be between 6 and 8 characters long";
            } else if (hasSpecialCharOrWhiteSpace) {
                errorMessage =
                    "User name must not contain spaces or special characters";
            }
        }
        if (name === "tin") {
            const valid = ssnValidation(value);
            errorMessage = valid
                ? ""
                : "Please enter a valid Social Security Number";
        }
        setErrors((prev) => ({ ...prev, [name]: errorMessage }));
    };

    const handleCancel = () => {
        document.location.href = ANNUITIES_FP_LOGIN_PAGE_URL_PATH;
    };

    const formatPayload = () => {
        return {
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email.trim(),
            id: user.id,
            tin: user.tin.replace(/-/g, ""),
            optionSelect: user.optionSelect,
            contractNumber: user.contractNumber,
            dob: user.dob ? moment(user.dob).format("YYYY-MM-DD") : "--",
            zipCode: user.zipCode.replace(/-/g, "")
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = formatPayload();
        setSubmitting(true);
        try {
            // console.log('Form submitted with data:', formatPayload());
            await axios({
                method: "POST",
                url: "/enroll/annuities/fp/register",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json"
                },
                data: JSON.stringify(payload)
            });
            document.location.href = REGISTRATION_SUCCESSFUL_URL_PATH;
        } catch (error) {
            setToastMessage(
                error.response?.data?.errorMessage ||
                    "Something went wrong. Please try again later."
            );
            window.scrollTo(0, 100);
        }
        setSubmitting(false);
    };

    const closeToast = () => {
        setToastMessage("");
    };

    return (
        <>
            {toastMessage !== "" && (
                <Toast onClose={closeToast} className="toast-cont">
                    <Toast.Header>
                        <strong className="me-auto">{toastMessage}</strong>
                    </Toast.Header>
                </Toast>
            )}
            <Header />
            <div className="mainMyAccount">
                <h1 className="heading">
                    Create a Financial Professional Account
                </h1>
                <div className="welcome-message">
                    <span>
                        <strong>
                            Registering for an online account gives you the
                            ability to:
                        </strong>
                    </span>
                    <ul>
                        <li>Manage your Pacific Life client contracts</li>
                        <li>Create hypothetical illustrations</li>
                        <li>View personalized content</li>
                        <li>Order literature</li>
                    </ul>
                    <div>
                        You must be appointed with Pacific Life to register to
                        the website. If you are not yet appointed, you can learn{" "}
                        <a href={ANNUITIES_GET_APPOINTED_URL}>
                            how to get appointed
                        </a>{" "}
                        or <a href={ANNUITIES_CONTACT_US_URL}>contact us</a> for
                        more information.
                    </div>
                </div>
                <Row>
                    <Col sm={6}>
                        <label className="sub-title">
                            Personal Information
                        </label>
                    </Col>
                </Row>
                <Form>
                    <Row>
                        <Col md={6}>
                            <Form.Label>First Name</Form.Label>
                            <Form.Control
                                name="firstName"
                                autoComplete="off"
                                maxLength={25}
                                value={user.firstName}
                                onChange={handleChange}
                            />
                        </Col>
                        <Col md={6}>
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control
                                name="lastName"
                                autoComplete="off"
                                maxLength={35}
                                value={user.lastName}
                                onChange={handleChange}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <ReactTooltip />
                            <Form.Label className="emailClass">
                                Email Address
                                <QuestionCircle
                                    tabIndex="-1"
                                    className="iconClass"
                                    data-tip="Your password link will be sent to the email address you supply here"
                                />
                            </Form.Label>
                            <Form.Control
                                name="email"
                                autoComplete="off"
                                maxLength={60}
                                value={user.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.email}
                            </span>
                        </Col>
                        <Col md={6}>
                            <Form.Label>Confirm Email Address</Form.Label>
                            <Form.Control
                                name="verifyEmail"
                                autoComplete="off"
                                maxLength={60}
                                value={user.verifyEmail}
                                onChange={handleChange}
                                onPaste={(e) => e.preventDefault()}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.verifyEmail}
                            </span>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={6}>
                            <label className="sub-title">
                                Identity Verification
                            </label>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <ReactTooltip />
                            <Form.Label className="socialSecurityNumber">
                                Social Security Number
                                <QuestionCircle
                                    tabIndex="-1"
                                    className="iconClass"
                                    data-tip="Enter your Social Security Number to verify your identity"
                                />
                            </Form.Label>
                            <div style={{ position: "relative" }}>
                                <Form.Control
                                    name="tin"
                                    type={showTin ? "text" : "password"}
                                    autoComplete="new-password"
                                    placeholder="123-45-6789"
                                    maxLength={11}
                                    value={user.tin ? formatSSN(user.tin) : ""}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                                    <span
                                        onClick={() => setShowTin((prev) => !prev)}
                                        style={{
                                            position: "absolute",
                                            right: "12px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            cursor: "pointer",
                                            color: "#495057"
                                        }}
                                        aria-label={showTin ? "Hide SSN" : "Show SSN"}
                                    >
                                        {showTin ? <EyeSlash size={20} /> : <Eye size={20} />}
                                    </span>
                                </div>
                            <span className="validation-error-message">
                                {errors.tin}
                            </span>
                        </Col>
                        <Col md={6} />
                    </Row>
                    <Form.Check
                        type="radio"
                        label="I have a client with an active Pacific Life annuity contract"
                        id="haveCN"
                        name="optionSelect"
                        value={"CN"}
                        onClick={handleChange}
                    />
                    {user.optionSelect === "CN" && (
                        <Row>
                            <Col md={6}>
                                <ReactTooltip />
                                <Form.Label className="contractNumber">
                                    Contract/Policy Number
                                    <QuestionCircle
                                        tabIndex="-1"
                                        className="iconClass"
                                        data-tip="A client annuity contract number from your Pacific Life book of business"
                                    />
                                </Form.Label>
                                <Form.Control
                                    placeholder="AB12345678"
                                    name="contractNumber"
                                    autoComplete="off"
                                    maxLength={10}
                                    value={user.contractNumber}
                                    onChange={handleChange}
                                />
                            </Col>
                        </Row>
                    )}
                    <Form.Check
                        type="radio"
                        label="I am appointed with Pacific Life, but not servicing any active contracts"
                        id="appointedNoneActive"
                        name="optionSelect"
                        value={"DOB"}
                        onClick={handleChange}
                    />
                    {user.optionSelect === "DOB" && (
                        <Row>
                            <Col md={6}>
                                <ReactTooltip />
                                <Form.Label>
                                    ZIP Code
                                    <QuestionCircle
                                        tabIndex="-1"
                                        className="iconClass"
                                        data-tip="Enter the five digit ZIP code of your primary residence"
                                    />
                                </Form.Label>
                                <Form.Control
                                    placeholder="12345"
                                    name="zipCode"
                                    autoComplete="off"
                                    maxLength={5}
                                    value={formatZip(user.zipCode)}
                                    onChange={handleChange}
                                />
                            </Col>
                            <Col md={5}>
                                <ReactTooltip />
                                <Form.Label>Date of Birth</Form.Label>
                                <DatePicker
                                    selected={user.dob}
                                    value={user.dob}
                                    className="react-datepicker-ignore-onclickoutside"
                                    onChange={(dob) =>
                                        handleChange({
                                            target: {
                                                name: "dob",
                                                value: dob
                                            }
                                        })
                                    }
                                    showYearDropdown
                                    showMonthDropdown
                                    dropdownMode="select"
                                    placeholderText="Click to select a date"
                                    maxDate={new Date()}
                                    openToDate={new Date("1985/01/01")}
                                    tabIndex={1}
                                />
                            </Col>
                        </Row>
                    )}
                    <Form.Check
                        type="radio"
                        label="I am not appointed with Pacific Life"
                        id="notAppointed"
                        name="optionSelect"
                        value={"notAppointed"}
                        onClick={handleChange}
                    />
                    {user.optionSelect === "notAppointed" && (
                        <Form.Label>
                            <div>
                                You may register online if you are appointed
                                with Pacific Life. If you are not yet appointed
                                with Pacific Life, you may learn{" "}
                                <a href={ANNUITIES_GET_APPOINTED_URL}>
                                    how to get appointed
                                </a>{" "}
                                or{" "}
                                <a href={ANNUITIES_CONTACT_US_URL}>
                                    contact us
                                </a>{" "}
                                for more information
                            </div>
                        </Form.Label>
                    )}
                    <Row>
                        <Col sm={6}>
                            <label className="sub-title">
                                Login Information
                            </label>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <ReactTooltip />
                            <Form.Label className="createUsername">
                                User Name
                                <QuestionCircle
                                    tabIndex="-1"
                                    className="iconClass"
                                    data-tip="Remember this! You will need it along with your password to access your account information"
                                />
                            </Form.Label>
                            <Form.Control
                                name="id"
                                autoComplete="off"
                                maxLength={8}
                                value={user.id}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.id}
                            </span>
                        </Col>
                        <Col md={6}>
                            <div className="sublabel">
                                Must be between 6 and 8 characters
                                <br />
                                May contain letters and numbers
                                <br />
                                No spaces or special characters
                            </div>
                        </Col>
                    </Row>
                    <div className="buttons-myaccount">
                        <Button
                            variant="secondary"
                            onClick={handleCancel}
                            onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                }
                            }}
                            data-track="analytics"
                        >
                            Cancel
                        </Button>{" "}
                        <Button
                            variant="primary"
                            type="submit"
                            onClick={handleSubmit}
                            data-track="analytics"
                            disabled={submitDisabled || submitting}
                        >
                            {submitting ? "Registering" : "Register"}
                        </Button>
                    </div>
                </Form>
                <div id="login" className="link-underline">
                    <a href={ANNUITIES_FP_LOGIN_PAGE_URL_PATH}>
                        Already have an account?
                    </a>
                </div>
                <Disclaimer />
            </div>
            <Footer />
        </>
    );
};

export default RetailReg;
